import { createContext, useContext, useEffect, useState } from "react";
import { trackAddToCart } from "@/lib/analytics";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("enoteca_cart")) || []; }
    catch { return []; }
  });
  const [discount, setDiscountState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("enoteca_discount")) || null; }
    catch { return null; }
  });
  const [giftBoxes, setGiftBoxesState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("enoteca_giftboxes")) || []; }
    catch { return []; }
  });
  const [premiumPackaging, setPremiumPackagingState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("enoteca_premium")) || false; }
    catch { return false; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("enoteca_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (discount) localStorage.setItem("enoteca_discount", JSON.stringify(discount));
    else localStorage.removeItem("enoteca_discount");
  }, [discount]);

  useEffect(() => {
    localStorage.setItem("enoteca_giftboxes", JSON.stringify(giftBoxes));
  }, [giftBoxes]);

  useEffect(() => {
    localStorage.setItem("enoteca_premium", JSON.stringify(premiumPackaging));
  }, [premiumPackaging]);

  const add = (wine, qty = 1) => {
    trackAddToCart(wine, qty);
    setItems((prev) => {
      const found = prev.find((i) => i.id === wine.id);
      if (found) return prev.map((i) => i.id === wine.id ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, { id: wine.id, name: wine.name, producer: wine.producer, price: wine.price, original_price: wine.original_price || null, image: wine.image, stock: wine.stock, type: wine.type, format: wine.format || "standard", quantity: qty }];
    });
    setOpen(true);
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const setQty = (id, qty) => setItems((prev) => prev.map((i) => i.id === id ? { ...i, quantity: Math.max(1, qty) } : i));
  const clear = () => { setItems([]); setDiscountState(null); setGiftBoxesState([]); setPremiumPackagingState(false); };

  const setDiscount = (d) => setDiscountState(d);
  const clearDiscount = () => setDiscountState(null);
  const setGiftBoxes = (b) => setGiftBoxesState(b || []);
  const setPremiumPackaging = (v) => setPremiumPackagingState(Boolean(v));

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const giftBoxesTotal = (giftBoxes || []).reduce((s, b) => s + (b.price || 0) * (b.quantity || 1), 0);
  const giftExtrasTotal = giftBoxesTotal + (premiumPackaging ? 3 : 0);
  const discountAmount = discount
    ? (discount.type === "percent" ? Math.round(total * discount.value) / 100 : Math.min(discount.value, total))
    : 0;

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, total, count, open, setOpen, discount, setDiscount, clearDiscount, discountAmount, giftBoxes, setGiftBoxes, premiumPackaging, setPremiumPackaging, giftBoxesTotal, giftExtrasTotal }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
